import { useCallback, useEffect, useReducer, useRef } from "react";

/**
 * Undo/Redo für Formularkorrekturen, gespeichert in localStorage (pro Anlage).
 *
 * - Schnelles Tippen im selben Feld (< 800 ms) wird zu einem Schritt zusammengefasst.
 * - Der Verlauf merkt sich die Server-Version. Hat jemand anders die Anlage inzwischen
 *   geändert, wird ein alter Verlauf beim Laden verworfen statt veraltete Werte zu zeigen.
 */

const MAX_SCHRITTE = 100;
const ZUSAMMENFASSEN_MS = 800;

export interface Verlauf<T> {
  vergangenheit: T[];
  gegenwart: T;
  zukunft: T[];
  version: number;
  letzteGruppe: string | null;
  letzteZeit: number;
}

export type VerlaufAktion<T> =
  | { art: "setzen"; wert: T; gruppe: string; zeit: number }
  | { art: "rueckgaengig" }
  | { art: "wiederholen" }
  | { art: "neueBasis"; wert: T; version: number; verlaufBehalten: boolean };

export function verlaufReduzieren<T>(zustand: Verlauf<T>, aktion: VerlaufAktion<T>): Verlauf<T> {
  switch (aktion.art) {
    case "setzen": {
      const zusammenfassen =
        aktion.gruppe === zustand.letzteGruppe && aktion.zeit - zustand.letzteZeit < ZUSAMMENFASSEN_MS;
      return {
        ...zustand,
        vergangenheit: zusammenfassen
          ? zustand.vergangenheit
          : [...zustand.vergangenheit, zustand.gegenwart].slice(-MAX_SCHRITTE),
        gegenwart: aktion.wert,
        zukunft: [],
        letzteGruppe: aktion.gruppe,
        letzteZeit: aktion.zeit,
      };
    }
    case "rueckgaengig": {
      const vorher = zustand.vergangenheit[zustand.vergangenheit.length - 1];
      if (vorher === undefined) return zustand;
      return {
        ...zustand,
        vergangenheit: zustand.vergangenheit.slice(0, -1),
        gegenwart: vorher,
        zukunft: [zustand.gegenwart, ...zustand.zukunft],
        letzteGruppe: null,
      };
    }
    case "wiederholen": {
      const naechster = zustand.zukunft[0];
      if (naechster === undefined) return zustand;
      return {
        ...zustand,
        vergangenheit: [...zustand.vergangenheit, zustand.gegenwart],
        gegenwart: naechster,
        zukunft: zustand.zukunft.slice(1),
        letzteGruppe: null,
      };
    }
    case "neueBasis":
      return {
        vergangenheit: aktion.verlaufBehalten ? zustand.vergangenheit : [],
        gegenwart: aktion.wert,
        zukunft: aktion.verlaufBehalten ? zustand.zukunft : [],
        version: aktion.version,
        letzteGruppe: null,
        letzteZeit: 0,
      };
  }
}

function laden<T>(schluessel: string, version: number): Verlauf<T> | null {
  try {
    const roh = localStorage.getItem(schluessel);
    if (!roh) return null;
    const gespeichert = JSON.parse(roh) as Verlauf<T>;
    return gespeichert.version === version ? { ...gespeichert, letzteGruppe: null, letzteZeit: 0 } : null;
  } catch {
    return null;
  }
}

function speichern<T>(schluessel: string, verlauf: Verlauf<T>): void {
  try {
    localStorage.setItem(schluessel, JSON.stringify(verlauf));
  } catch {
    /* Speicher voll oder gesperrt – Undo funktioniert weiter, nur nicht über Neuladen hinweg */
  }
}

export function verlaufLoeschen(schluessel: string): void {
  try {
    localStorage.removeItem(schluessel);
  } catch {
    /* ignorieren */
  }
}

export const VERLAUF_PRAEFIX = "anmeldemeister.korrekturen.";

/** Beim Abmelden: alle gespeicherten Korrektur-Verläufe (können personenbezogene Daten enthalten) entfernen. */
export function alleVerlaeufeLoeschen(): void {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(VERLAUF_PRAEFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignorieren */
  }
}

export function useUndoRedo<T>(schluessel: string, initial: T, version: number, persistieren = true) {
  const [zustand, dispatch] = useReducer(
    verlaufReduzieren<T>,
    null,
    (): Verlauf<T> =>
      laden<T>(schluessel, version) ?? {
        vergangenheit: [],
        gegenwart: initial,
        zukunft: [],
        version,
        letzteGruppe: null,
        letzteZeit: 0,
      },
  );

  const ersterLauf = useRef(true);
  useEffect(() => {
    if (!persistieren) {
      verlaufLoeschen(schluessel);
      return;
    }
    if (ersterLauf.current) {
      ersterLauf.current = false;
      if (zustand.vergangenheit.length === 0 && zustand.zukunft.length === 0) return;
    }
    speichern(schluessel, zustand);
  }, [schluessel, zustand, persistieren]);

  const setzen = useCallback((wert: T, gruppe: string) => dispatch({ art: "setzen", wert, gruppe, zeit: Date.now() }), []);
  const rueckgaengig = useCallback(() => dispatch({ art: "rueckgaengig" }), []);
  const wiederholen = useCallback(() => dispatch({ art: "wiederholen" }), []);
  const neueBasis = useCallback(
    (wert: T, neueVersion: number, verlaufBehalten = true) =>
      dispatch({ art: "neueBasis", wert, version: neueVersion, verlaufBehalten }),
    [],
  );

  return {
    werte: zustand.gegenwart,
    version: zustand.version,
    setzen,
    rueckgaengig,
    wiederholen,
    neueBasis,
    kannRueckgaengig: zustand.vergangenheit.length > 0,
    kannWiederholen: zustand.zukunft.length > 0,
    wiederhergestellt: zustand.version === version && zustand.vergangenheit.length > 0,
  };
}
